import React, { useState, useRef, useEffect } from "react";

interface VideoFrameSelectorProps {
  videoUrl: string;
  onFrameSelect: (frameBase64: string) => void;
  onClose: () => void;
}

const VideoFrameSelector: React.FC<VideoFrameSelectorProps> = ({
  videoUrl,
  onFrameSelect,
  onClose,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  // const [duration, setDuration] = useState(0);
  // const [isPlaying, setIsPlaying] = useState(false);
  // const [selectedFrame, setSelectedFrame] = useState<string | null>(null);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [localVideoUrl, setLocalVideoUrl] = useState<string | null>(null);

  // Fetch video as blob and create local URL to avoid CORS issues
  useEffect(() => {
    let currentLocalUrl: string | null = null;

    const fetchVideoAsBlob = async () => {
      try {
        setVideoLoading(true);
        setVideoError(null);

        console.log("Attempting to fetch video as blob:", videoUrl);

        // Add cache-busting parameter to force fresh CORS preflight
        const cacheBustedVideoUrl = videoUrl.includes("?")
          ? `${videoUrl}&v=2`
          : `${videoUrl}?v=2`;

        const response = await fetch(cacheBustedVideoUrl, {
          mode: "cors",
          credentials: "omit",
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const blob = await response.blob();
        const localUrl = URL.createObjectURL(blob);
        currentLocalUrl = localUrl;
        setLocalVideoUrl(localUrl);
        setVideoLoading(false);
        console.log("Video blob created successfully, size:", blob.size);
      } catch (error) {
        console.error("Error fetching video as blob:", error);
        console.log("Falling back to direct video URL");
        // Fallback to direct URL - video will load but frame extraction may fail
        setLocalVideoUrl(null);
        setVideoLoading(false);
      }
    };

    fetchVideoAsBlob();

    // Cleanup function
    return () => {
      if (currentLocalUrl) {
        URL.revokeObjectURL(currentLocalUrl);
      }
    };
  }, [videoUrl]);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      // setDuration(video.duration);
      setVideoLoading(false);
      setVideoError(null);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    // const handlePlay = () => setIsPlaying(true);
    // const handlePause = () => setIsPlaying(false);

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("timeupdate", handleTimeUpdate);
    // video.addEventListener("play", handlePlay);
    // video.addEventListener("pause", handlePause);

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("timeupdate", handleTimeUpdate);
      // video.removeEventListener("play", handlePlay);
      // video.removeEventListener("pause", handlePause);
    };
  }, []);

  const captureFrame = async (): Promise<string | null> => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return null;

    try {
      // Wait for video to be ready
      if (video.readyState < 2) {
        await new Promise((resolve) => {
          const onLoadedData = () => {
            video.removeEventListener("loadeddata", onLoadedData);
            resolve(void 0);
          };
          video.addEventListener("loadeddata", onLoadedData);
        });
      }

      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Could not get canvas context");

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 360;

      // Draw the current frame
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert to base64
      const base64 = canvas.toDataURL("image/jpeg", 0.8);
      console.log("Frame captured successfully, base64 length:", base64.length);
      console.log(
        "Using video source:",
        localVideoUrl ? "blob URL" : "direct URL"
      );
      return base64;
    } catch (error) {
      console.error("Frame capture error:", error);

      if (error instanceof DOMException && error.name === "SecurityError") {
        if (localVideoUrl) {
          setVideoError(
            "Frame extraction failed even with blob URL. Video may have additional restrictions."
          );
        } else {
          setVideoError(
            "Frame extraction failed due to CORS. Blob fetch also failed for this video."
          );
        }
      } else {
        setVideoError("Failed to capture frame. Please try again.");
      }
      return null;
    }
  };

  const handlePresetFrame = async (
    preset: "first" | "last"
  ): Promise<string | null> => {
    const video = videoRef.current;
    if (!video) return null;

    const time = preset === "first" ? 0.1 : Math.max(0, video.duration - 0.1);

    // Pause video first
    video.pause();

    // Set the time and wait for seek to complete
    video.currentTime = time;

    await new Promise((resolve) => {
      const onSeeked = () => {
        video.removeEventListener("seeked", onSeeked);
        resolve(void 0);
      };
      video.addEventListener("seeked", onSeeked);

      // Fallback timeout in case seeked event doesn't fire
      setTimeout(resolve, 500);
    });

    // Now capture the frame
    try {
      const frame = await captureFrame();
      if (frame) {
        console.log(`${preset} frame captured successfully`);
        return frame;
      } else {
        console.error(`Failed to capture ${preset} frame`);
        return null;
      }
    } catch (error) {
      console.error("Error capturing preset frame:", error);
      return null;
    }
  };

  // const handleScrubberChange = async (
  //   e: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   const video = videoRef.current;
  //   if (!video) return;

  //   const time = parseFloat(e.target.value);
  //   setCurrentTime(time);

  //   // Pause video and seek to the time
  //   video.pause();
  //   video.currentTime = time;

  //   // Wait for seek to complete, then capture frame
  //   await new Promise((resolve) => {
  //     const onSeeked = () => {
  //       video.removeEventListener("seeked", onSeeked);
  //       resolve(void 0);
  //     };
  //     video.addEventListener("seeked", onSeeked);

  //     // Fallback timeout
  //     setTimeout(resolve, 200);
  //   });

  //   try {
  //     const frame = await captureFrame();
  //     if (frame) {
  //       setSelectedFrame(frame);
  //       console.log("Frame captured at time:", time);
  //     }
  //   } catch (error) {
  //     console.error("Error capturing frame during scrub:", error);
  //   }
  // };

  // const handleConfirmFrame = () => {
  //   if (selectedFrame) {
  //     onFrameSelect(selectedFrame);
  //     onClose();
  //   }
  // };

  // const formatTime = (time: number) => {
  //   const minutes = Math.floor(time / 60);
  //   const seconds = Math.floor(time % 60);
  //   return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  // };

  // Cache-busted URL for video element fallback
  const cacheBustedVideoUrl = videoUrl.includes("?")
    ? `${videoUrl}&v=2`
    : `${videoUrl}?v=2`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Select Frame</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg
              className="w-6 h-6 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Status Notice */}
          <div
            className={`${
              localVideoUrl
                ? "bg-green-50 border-green-200"
                : "bg-yellow-50 border-yellow-200"
            } border rounded-lg p-4`}
          >
            <div className="flex items-start gap-3">
              <svg
                className={`w-5 h-5 ${
                  localVideoUrl ? "text-green-600" : "text-yellow-600"
                } mt-0.5`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <h4
                  className={`font-medium ${
                    localVideoUrl ? "text-green-800" : "text-yellow-800"
                  }`}
                >
                  {localVideoUrl
                    ? "Frame Extraction Available"
                    : "Limited Frame Extraction"}
                </h4>
                <p
                  className={`${
                    localVideoUrl ? "text-green-700" : "text-yellow-700"
                  } text-sm mt-1`}
                >
                  {localVideoUrl
                    ? "Video downloaded successfully. You can extract frames from any point in the video."
                    : "Video loaded directly. Frame extraction may be limited due to CORS restrictions."}
                </p>
              </div>
            </div>
          </div>

          {/* Video Player */}
          <div className="relative">
            {videoError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 text-red-700">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="font-medium">Video Loading Error</span>
                </div>
                <p className="text-red-600 text-sm mt-1">{videoError}</p>
                <p className="text-red-600 text-xs mt-2">URL: {videoUrl}</p>
              </div>
            )}

            {videoLoading && (
              <div className="bg-gray-100 rounded-lg flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-sm text-gray-600">Downloading video...</p>
                </div>
              </div>
            )}

            {(localVideoUrl || (!videoLoading && !videoError)) && (
              <video
                ref={videoRef}
                src={localVideoUrl || cacheBustedVideoUrl}
                className="w-full max-h-64 rounded-lg bg-gray-900"
                controls
                preload="metadata"
                crossOrigin="anonymous"
                onLoadedMetadata={async () => {
                  console.log(
                    "Video loaded, duration:",
                    videoRef.current?.duration
                  );
                  // Auto-capture first frame when video loads
                  try {
                    // Wait a bit for the video to be fully ready
                    await new Promise((resolve) => setTimeout(resolve, 500));
                    // Seek to first frame first
                    if (videoRef.current) {
                      videoRef.current.currentTime = 0.1;
                      await new Promise((resolve) => setTimeout(resolve, 200));
                    }
                    const frame = await captureFrame();
                    if (frame) {
                      // setSelectedFrame(frame);
                      console.log("Initial frame captured successfully");
                    }
                  } catch (error) {
                    console.error("Error capturing initial frame:", error);
                  }
                }}
                onLoadedData={() => {
                  console.log("Video data loaded");
                }}
                onCanPlay={() => {
                  console.log("Video can play");
                }}
                onError={(e) => {
                  console.error("Video loading error:", e);
                  console.error("Video URL:", videoUrl);
                  setVideoError(
                    "Failed to load video. The video may be corrupted or the URL is invalid."
                  );
                  setVideoLoading(false);
                }}
              />
            )}
            <canvas ref={canvasRef} className="hidden" />
          </div>

          {/* Simple Frame Selection */}
          <div className="text-center space-y-4">
            <p className="text-gray-600 text-sm">
              Choose which frame to use from the video
            </p>

            <div className="flex justify-center items-center gap-4">
              <button
                onClick={async () => {
                  const frame = await handlePresetFrame("first");
                  if (frame) {
                    onFrameSelect(frame);
                    onClose();
                  }
                }}
                className="flex items-center gap-2 p-3 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <div className="w-8 h-8 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 19l-7-7 7-7"
                    />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-700">First</span>
              </button>

              <button
                onClick={async () => {
                  const frame = await captureFrame();
                  if (frame) {
                    console.log(
                      `Current frame captured at time: ${currentTime.toFixed(
                        1
                      )}s`
                    );
                    onFrameSelect(frame);
                    onClose();
                  }
                }}
                className="bg-blue-600 text-white hover:bg-blue-700 px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                Select Frame
              </button>

              <button
                onClick={async () => {
                  const frame = await handlePresetFrame("last");
                  if (frame) {
                    onFrameSelect(frame);
                    onClose();
                  }
                }}
                className="flex items-center gap-2 p-3 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <span className="text-sm font-medium text-gray-700">Last</span>
                <div className="w-8 h-8 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </button>
            </div>
          </div>

          {/* Action Button */}
          <div className="text-center pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoFrameSelector;
